import { LOCALES, LOCALE_LABEL } from '@oxyflows/i18n';
import { POLICY_VERSION } from '@oxyflows/contracts';
import { useI18n } from '../i18n';
import { Logo } from '../brand';

/**
 * The data policy at /policy.
 *
 * Public, in both languages, and what the clinic owner accepts on the clinic's
 * behalf when activating it — before a single patient record is stored. The
 * first clinics are in Syria and the service is hosted in the European Union,
 * so the sentence that matters most is the one in "Where the information is
 * stored": the clinic agrees to that, knowingly, and the API records when and
 * which version (POLICY_VERSION in packages/contracts).
 *
 * The wording is kept here rather than in the i18n dictionary: it is a
 * document with a version, not interface text, and it must read as one piece
 * in each language.
 */
interface Section {
  h: string;
  p?: string[];
  list?: string[];
  after?: string[];
}

interface PolicyText {
  title: string;
  version: string;
  intro: string;
  sections: Section[];
  back: string;
}

const TEXT: Record<'en' | 'ar', PolicyText> = {
  en: {
    title: 'Data & privacy policy',
    version: `Version ${POLICY_VERSION}`,
    intro:
      "OxyFlows is a service for dental clinics. This page says what happens to the information a clinic keeps in it, where that information is stored, and what each side is responsible for. The clinic owner accepts this policy on the clinic's behalf when activating the clinic, before any patient record is stored.",
    sections: [
      {
        h: 'Who is responsible for what',
        p: [
          "The clinic decides what it records about its patients and why, and it remains responsible for that information. OxyFlows stores and processes it only to provide the service and only on the clinic's instructions.",
          "OxyFlows staff do not read patient records. The operator console we use to manage clinics cannot open them. Support access to a clinic is given only with the clinic's agreement, is limited in time, and is recorded.",
        ],
      },
      {
        h: 'Where the information is stored',
        p: [
          'The service runs on servers in the European Union (Frankfurt, Germany). Uploaded files such as x-rays, photographs, scans and documents are kept in storage located in the European Union, and so are the backups. Information travels between the clinic and the servers encrypted.',
          'By accepting this policy, the clinic agrees that its records, including patient records, are stored outside its own country, in the European Union, as described here, and confirms that it may do so under its obligations to its patients and under the law that applies to it.',
        ],
      },
      {
        h: 'What is stored',
        list: [
          "The clinic's details and its staff accounts.",
          'Patient records: identity and contact details, medical history, the dental chart, treatments, clinical notes, prescriptions, images and documents.',
          'Appointments and booking requests.',
          "Invoices, payments and other financial records, including dentists' agreements and settlements.",
          'Inventory, purchasing and laboratory records.',
          'A log of who did what in the system, and when.',
        ],
      },
      {
        h: 'How it is protected',
        list: [
          "Each clinic's information is separated from every other clinic's inside the database itself, not only by the application.",
          'Access needs a personal account and password. The clinic owner sets what each role and each person may see and do.',
          'Clinical notes, financial entries and the activity log cannot be changed or deleted once written; a correction is added as an amendment or a reversal, and the original stays visible.',
          'Every sign-in and every change is logged. Backups are taken automatically.',
        ],
      },
      {
        h: 'Who else handles the information',
        p: ['To provide the service, OxyFlows uses these providers, each of which holds only what its part requires:'],
        list: [
          'Render, which runs the application, in the European Union.',
          'Neon, which holds the database, in the European Union.',
          'Cloudflare, which carries the network traffic and stores uploaded files.',
          'An email delivery service, which sends sign-in codes and notifications to the addresses the clinic gives us.',
        ],
        after: ['A change of provider is announced on this page before it takes effect.'],
      },
      {
        h: 'How long it is kept',
        p: [
          "For as long as the clinic's subscription is active, and for ninety days after it ends, so that the clinic can renew or ask for an export.",
          "After that, the clinic's complete data is exported to an archive file and removed from the live servers. The archive is kept so that the clinic can return; the clinic can ask in writing for it to be deleted.",
        ],
      },
      {
        h: 'Export and deletion',
        p: [
          'The clinic can export its records from the application at any time in standard spreadsheet formats, and can ask OxyFlows for a complete archive of everything it holds. It can also ask for the clinic to be removed from the servers.',
        ],
      },
      {
        h: "The clinic's part",
        list: [
          "Obtain its patients' consent where the law that applies to it requires.",
          'Give every member of staff their own account, and remove the accounts of people who leave.',
          'Keep passwords private, and use two-step sign-in when it is available.',
          'Tell OxyFlows promptly of any suspected misuse of an account.',
        ],
      },
      {
        h: 'If something goes wrong',
        p: [
          "If OxyFlows learns of unauthorised access to a clinic's information, it informs the clinic without undue delay, and no later than 72 hours after confirming it, with what is known and what is being done.",
        ],
      },
      {
        h: 'Changes to this policy',
        p: [
          'This policy carries a version date. A change that matters is sent to the clinic owner, and the clinic is asked to accept it again. Earlier versions are available on request.',
        ],
      },
      {
        h: 'Questions',
        p: ['Contact the OxyFlows operator who set up your clinic. A dedicated address will be published on this page.'],
      },
    ],
    back: 'Back to sign in',
  },
  ar: {
    title: 'سياسة البيانات والخصوصية',
    version: `الإصدار ${POLICY_VERSION}`,
    intro:
      'أوكسي فلوز خدمة لعيادات الأسنان. تبيّن هذه الصفحة ما يحدث للمعلومات التي تحفظها العيادة فيها، وأين تُخزَّن هذه المعلومات، وما مسؤولية كل طرف. يقبل مالك العيادة هذه السياسة بالنيابة عن العيادة عند تفعيلها، قبل تخزين أي سجل مريض.',
    sections: [
      {
        h: 'مسؤولية كل طرف',
        p: [
          'العيادة هي التي تقرر ما تسجّله عن مرضاها ولماذا، وتبقى مسؤولة عن هذه المعلومات. تخزّن أوكسي فلوز المعلومات وتعالجها فقط لتقديم الخدمة وبناءً على تعليمات العيادة.',
          'لا يطّلع موظفو أوكسي فلوز على سجلات المرضى. ولوحة التشغيل التي نُدير بها العيادات لا تستطيع فتحها. يُمنح الدعم الفني صلاحية الدخول إلى عيادة بموافقتها فقط، ولمدة محدودة، ويُسجَّل ذلك.',
        ],
      },
      {
        h: 'أين تُخزَّن المعلومات',
        p: [
          'تعمل الخدمة على خوادم في الاتحاد الأوروبي (فرانكفورت، ألمانيا). تُحفظ الملفات المرفوعة مثل صور الأشعة والصور الفوتوغرافية والمسوحات والمستندات في مخازن تقع في الاتحاد الأوروبي، وكذلك النسخ الاحتياطية. وتنتقل المعلومات بين العيادة والخوادم مشفّرة.',
          'بقبول هذه السياسة توافق العيادة على تخزين سجلاتها، بما فيها سجلات المرضى، خارج بلدها، في الاتحاد الأوروبي، كما هو موضح هنا، وتؤكد أن ذلك جائز لها بموجب التزاماتها تجاه مرضاها والقانون المطبَّق عليها.',
        ],
      },
      {
        h: 'ما الذي يُخزَّن',
        list: [
          'بيانات العيادة وحسابات موظفيها.',
          'سجلات المرضى: الهوية وبيانات الاتصال، والتاريخ الطبي، ومخطط الأسنان، والمعالجات، والملاحظات السريرية، والوصفات، والصور والمستندات.',
          'المواعيد وطلبات الحجز.',
          'الفواتير والمدفوعات والسجلات المالية الأخرى، بما فيها اتفاقيات الأطباء وتسوياتهم.',
          'سجلات المخزون والمشتريات والمختبر.',
          'سجل بمن فعل ماذا في النظام ومتى.',
        ],
      },
      {
        h: 'كيف تُحمى',
        list: [
          'تُفصل معلومات كل عيادة عن معلومات غيرها داخل قاعدة البيانات نفسها، لا في التطبيق فقط.',
          'يتطلب الدخول حسابًا شخصيًا وكلمة مرور. ويحدد مالك العيادة ما يراه ويفعله كل دور وكل شخص.',
          'لا يمكن تعديل الملاحظات السريرية والقيود المالية وسجل النشاط أو حذفها بعد كتابتها؛ يُضاف التصحيح كتعديل لاحق أو عكس قيد، ويبقى الأصل ظاهرًا.',
          'يُسجَّل كل دخول وكل تغيير. وتُؤخذ النسخ الاحتياطية تلقائيًا.',
        ],
      },
      {
        h: 'من غيرنا يتعامل مع المعلومات',
        p: ['لتقديم الخدمة تستعين أوكسي فلوز بالمزوّدين التاليين، ولا يحتفظ كل منهم إلا بما يتطلبه دوره:'],
        list: [
          'Render، الذي يشغّل التطبيق، في الاتحاد الأوروبي.',
          'Neon، الذي يستضيف قاعدة البيانات، في الاتحاد الأوروبي.',
          'Cloudflare، الذي ينقل حركة الشبكة ويخزّن الملفات المرفوعة.',
          'خدمة إرسال بريد إلكتروني، ترسل رموز الدخول والإشعارات إلى العناوين التي تزوّدنا بها العيادة.',
        ],
        after: ['يُعلَن عن أي تغيير في المزوّدين في هذه الصفحة قبل سريانه.'],
      },
      {
        h: 'مدة الاحتفاظ',
        p: [
          'ما دام اشتراك العيادة ساريًا، ولتسعين يومًا بعد انتهائه، لتتمكن العيادة من التجديد أو طلب تصدير بياناتها.',
          'بعد ذلك تُصدَّر بيانات العيادة كاملةً إلى ملف أرشيف وتُزال من الخوادم العاملة. يُحتفظ بالأرشيف ليتسنى للعيادة العودة، ويمكنها أن تطلب كتابةً حذفه.',
        ],
      },
      {
        h: 'التصدير والحذف',
        p: [
          'تستطيع العيادة تصدير سجلاتها من التطبيق في أي وقت بصيغ جداول قياسية، وأن تطلب من أوكسي فلوز أرشيفًا كاملًا لكل ما تحتفظ به. ويمكنها أيضًا طلب إزالة العيادة من الخوادم.',
        ],
      },
      {
        h: 'دور العيادة',
        list: [
          'الحصول على موافقة مرضاها حيث يتطلب القانون المطبَّق عليها ذلك.',
          'منح كل موظف حسابه الخاص، وإزالة حسابات من يغادرون.',
          'الحفاظ على سرية كلمات المرور، واستخدام الدخول بخطوتين عند توفره.',
          'إبلاغ أوكسي فلوز فورًا عند الاشتباه بإساءة استخدام أي حساب.',
        ],
      },
      {
        h: 'إذا حدث خلل',
        p: [
          'إذا علمت أوكسي فلوز بوصول غير مصرح به إلى معلومات عيادة، فإنها تبلغ العيادة دون تأخير، وفي مدة لا تتجاوز 72 ساعة من تأكيد الحادثة، بما هو معروف وما يجري القيام به.',
        ],
      },
      {
        h: 'التغييرات على هذه السياسة',
        p: [
          'تحمل هذه السياسة تاريخ إصدار. يُرسَل أي تغيير جوهري إلى مالك العيادة، ويُطلب من العيادة قبوله من جديد. والإصدارات السابقة متاحة عند الطلب.',
        ],
      },
      {
        h: 'للاستفسار',
        p: ['تواصل مع مشغّل أوكسي فلوز الذي أنشأ عيادتك. وسيُنشر عنوان مخصص في هذه الصفحة.'],
      },
    ],
    back: 'العودة إلى تسجيل الدخول',
  },
};

export function Policy() {
  const { locale, setLocale } = useI18n();
  const d = TEXT[locale];
  return (
    <div className="public-shell">
      <header className="public-bar">
        <div className="public-inner">
          <a href="/" aria-label="OxyFlows">
            <Logo className="public-bar-logo" />
          </a>
          <span className="row" style={{ gap: 10 }}>
            {LOCALES.map((l) => (
              <button
                key={l}
                type="button"
                className="btn-link"
                style={l === locale ? { fontWeight: 600 } : undefined}
                onClick={() => setLocale(l)}
              >
                {LOCALE_LABEL[l]}
              </button>
            ))}
          </span>
        </div>
      </header>
      <main className="public-body">
        <article className="public-inner" style={{ maxWidth: 800 }}>
          <h1>{d.title}</h1>
          <p className="page-sub">{d.version}</p>
          <p>{d.intro}</p>
          {d.sections.map((s) => (
            <section key={s.h} style={{ marginTop: 26 }}>
              <h2 className="public-h2">{s.h}</h2>
              {s.p?.map((text) => (
                <p key={text}>{text}</p>
              ))}
              {s.list ? (
                <ul>
                  {s.list.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              ) : null}
              {s.after?.map((text) => (
                <p key={text}>{text}</p>
              ))}
            </section>
          ))}
          <p style={{ marginTop: 32 }}>
            <a className="btn-link" href="/">
              {d.back}
            </a>
          </p>
        </article>
      </main>
    </div>
  );
}
